/**
 * Date utilities for SPLH Dashboard using date-fns
 *
 * All date manipulation in the SPLH Dashboard MUST use these utilities.
 * Do not use vanilla JavaScript Date methods - use date-fns functions instead.
 *
 * @module pages/common/dashboards/SPLHDashboard/utils/dateUtils
 */

import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  differenceInDays,
  isValid,
  startOfDay,
  endOfDay,
} from 'date-fns';

/**
 * Date range presets supported by the dashboard
 * @constant {Array<string>}
 */
export const DATE_PRESETS = [
  'today',
  'yesterday',
  'last7days',
  'last30days',
  'mtd',
  'custom',
];

/**
 * Gets a date range based on a preset key
 *
 * @param {string} preset - The preset key ('today', 'yesterday', 'last7days', 'last30days', 'mtd', 'custom')
 * @param {Object} [customRange] - Custom date range when preset is 'custom' (optional)
 * @param {Date|string} [customRange.startDate] - Start date for custom range
 * @param {Date|string} [customRange.endDate] - End date for custom range
 * @returns {{ startDate: string, endDate: string }} Date range object with ISO date strings (YYYY-MM-DD)
 * @throws {Error} If preset is invalid or custom range is missing when preset is 'custom'
 *
 * @example
 * // Get last 7 days range
 * const range = getPresetDateRange('last7days');
 * // { startDate: '2025-11-19', endDate: '2025-11-25' }
 *
 * @example
 * // Get custom range
 * const range = getPresetDateRange('custom', {
 *   startDate: new Date('2025-11-01'),
 *   endDate: new Date('2025-11-15')
 * });
 */
export function getPresetDateRange(preset, customRange = null) {
  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);

  switch (preset) {
    case 'today':
      return {
        startDate: formatDateForAPI(today),
        endDate: formatDateForAPI(today),
      };

    case 'yesterday':
      return {
        startDate: formatDateForAPI(yesterday),
        endDate: formatDateForAPI(yesterday),
      };

    case 'last7days':
      return {
        startDate: formatDateForAPI(subDays(yesterday, 6)),
        endDate: formatDateForAPI(yesterday),
      };

    case 'last30days':
      return {
        startDate: formatDateForAPI(subDays(yesterday, 29)),
        endDate: formatDateForAPI(yesterday),
      };

    case 'mtd':
      return {
        startDate: formatDateForAPI(startOfMonth(today)),
        endDate: formatDateForAPI(yesterday),
      };

    case 'custom':
      if (!customRange || !customRange.startDate || !customRange.endDate) {
        throw new Error(
          'Custom range requires startDate and endDate to be provided'
        );
      }
      return {
        startDate: formatDateForAPI(customRange.startDate),
        endDate: formatDateForAPI(customRange.endDate),
      };

    default:
      throw new Error(`Invalid date preset: ${preset}`);
  }
}

/**
 * Formats a date for API consumption (ISO 8601 date format: YYYY-MM-DD)
 *
 * @param {Date|string} date - The date to format (Date object or ISO string)
 * @returns {string} Formatted date string in YYYY-MM-DD format
 * @throws {Error} If date is invalid
 *
 * @example
 * formatDateForAPI(new Date('2025-11-25'));
 * // Returns: '2025-11-25'
 *
 * @example
 * formatDateForAPI('2025-11-25T12:00:00.000Z');
 * // Returns: '2025-11-25'
 */
export function formatDateForAPI(date) {
  if (!date) {
    throw new Error('Date is required');
  }

  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  return format(dateObj, 'yyyy-MM-dd');
}

/**
 * Formats a date for user-friendly display
 *
 * @param {Date|string} date - The date to format (Date object or ISO string)
 * @param {string} [formatStr='MMM dd, yyyy'] - date-fns format string
 * @returns {string} Formatted date string for display
 *
 * @example
 * formatDateForDisplay(new Date('2025-11-25'));
 * // Returns: 'Nov 25, 2025'
 *
 * @example
 * formatDateForDisplay('2025-11-25', 'MMMM d, yyyy');
 * // Returns: 'November 25, 2025'
 */
export function formatDateForDisplay(date, formatStr = 'MMM dd, yyyy') {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    return '';
  }

  return format(dateObj, formatStr);
}

/**
 * Gets the start and end dates of the week containing the given date
 *
 * @param {Date|string} date - Reference date (Date object or ISO string)
 * @param {Object} [options] - Options object
 * @param {number} [options.weekStartsOn=1] - Day the week starts on (0=Sunday, 1=Monday, etc.)
 * @returns {{ startDate: string, endDate: string }} Week range with ISO date strings
 *
 * @example
 * // For a Wednesday in November 2025
 * getWeekRange(new Date('2025-11-26'));
 * // Returns: { startDate: '2025-11-24', endDate: '2025-11-30' } (Mon-Sun)
 *
 * @example
 * // Week starting on Sunday
 * getWeekRange(new Date('2025-11-26'), { weekStartsOn: 0 });
 * // Returns: { startDate: '2025-11-23', endDate: '2025-11-29' } (Sun-Sat)
 */
export function getWeekRange(date, options = {}) {
  const { weekStartsOn = 1 } = options; // Default to Monday

  if (!date) {
    throw new Error('Date is required');
  }

  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  const weekStart = startOfWeek(dateObj, { weekStartsOn });
  const weekEnd = endOfWeek(dateObj, { weekStartsOn });

  return {
    startDate: formatDateForAPI(weekStart),
    endDate: formatDateForAPI(weekEnd),
  };
}

/**
 * Gets the start and end dates of the month containing the given date
 *
 * @param {Date|string} date - Reference date (Date object or ISO string)
 * @returns {{ startDate: string, endDate: string }} Month range with ISO date strings
 *
 * @example
 * getMonthRange(new Date('2025-11-15'));
 * // Returns: { startDate: '2025-11-01', endDate: '2025-11-30' }
 */
export function getMonthRange(date) {
  if (!date) {
    throw new Error('Date is required');
  }

  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(dateObj)) {
    throw new Error(`Invalid date: ${date}`);
  }

  const monthStart = startOfMonth(dateObj);
  const monthEnd = endOfMonth(dateObj);

  return {
    startDate: formatDateForAPI(monthStart),
    endDate: formatDateForAPI(monthEnd),
  };
}

/**
 * Calculates the number of days in a date range (inclusive)
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {number} Number of days in the range (inclusive)
 *
 * @example
 * getDaysInRange('2025-11-01', '2025-11-07');
 * // Returns: 7
 */
export function getDaysInRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = parseISO(startDate);
  const end = parseISO(endDate);

  if (!isValid(start) || !isValid(end)) {
    return 0;
  }

  return differenceInDays(end, start) + 1;
}

/**
 * Formats a date range for display
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {string} Formatted date range string
 *
 * @example
 * formatDateRangeForDisplay('2025-11-01', '2025-11-30');
 * // Returns: 'Nov 01 - Nov 30, 2025'
 *
 * @example
 * // Single day
 * formatDateRangeForDisplay('2025-11-25', '2025-11-25');
 * // Returns: 'Nov 25, 2025'
 */
export function formatDateRangeForDisplay(startDate, endDate) {
  if (!startDate) {
    return 'No date selected';
  }

  const start = parseISO(startDate);
  const end = endDate ? parseISO(endDate) : start;

  if (!isValid(start)) {
    return 'Invalid date';
  }

  // Single day
  if (startDate === endDate) {
    return format(start, 'MMM dd, yyyy');
  }

  // Same year
  if (format(start, 'yyyy') === format(end, 'yyyy')) {
    // Same month
    if (format(start, 'MMM') === format(end, 'MMM')) {
      return `${format(start, 'MMM dd')} - ${format(end, 'dd, yyyy')}`;
    }
    return `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`;
  }

  // Different years
  return `${format(start, 'MMM dd, yyyy')} - ${format(end, 'MMM dd, yyyy')}`;
}

/**
 * Validates if a date string is in valid YYYY-MM-DD format
 *
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} True if valid, false otherwise
 *
 * @example
 * isValidDateString('2025-11-25'); // true
 * isValidDateString('invalid'); // false
 * isValidDateString('11-25-2025'); // false
 */
export function isValidDateString(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') {
    return false;
  }

  // Check format matches YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) {
    return false;
  }

  // Check if it parses to a valid date
  const parsed = parseISO(dateStr);
  return isValid(parsed);
}

/**
 * Validates a date range object
 *
 * @param {{ startDate: string, endDate: string }} dateRange - Date range to validate
 * @returns {{ valid: boolean, error: string|null }} Validation result
 *
 * @example
 * validateDateRange({ startDate: '2025-11-01', endDate: '2025-11-30' });
 * // Returns: { valid: true, error: null }
 *
 * @example
 * validateDateRange({ startDate: '2025-11-30', endDate: '2025-11-01' });
 * // Returns: { valid: false, error: 'Start date must be before or equal to end date' }
 */
export function validateDateRange(dateRange) {
  if (!dateRange) {
    return { valid: false, error: 'Date range is required' };
  }

  const { startDate, endDate } = dateRange;

  if (!startDate) {
    return { valid: false, error: 'Start date is required' };
  }

  if (!endDate) {
    return { valid: false, error: 'End date is required' };
  }

  if (!isValidDateString(startDate)) {
    return { valid: false, error: 'Invalid start date format' };
  }

  if (!isValidDateString(endDate)) {
    return { valid: false, error: 'Invalid end date format' };
  }

  const start = parseISO(startDate);
  const end = parseISO(endDate);

  if (start > end) {
    return {
      valid: false,
      error: 'Start date must be before or equal to end date',
    };
  }

  return { valid: true, error: null };
}

/**
 * Gets today's date in API format
 *
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getTodayForAPI() {
  return formatDateForAPI(new Date());
}

/**
 * Gets yesterday's date in API format
 *
 * @returns {string} Yesterday's date in YYYY-MM-DD format
 */
export function getYesterdayForAPI() {
  return formatDateForAPI(subDays(new Date(), 1));
}
